import { useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import Base from './Base';
import { MapPin, MailOpen, Phone } from 'lucide-react';
import AuthContext from '../context/AuthContext';
import axios from 'axios';

const Contact = ({ success }) => {
  const navigate = useNavigate();
  const { authToken } = useContext(AuthContext);

  useEffect(() => {
    if (success) {
      Swal.fire({
        icon: 'success',
        title: 'Message Sent!',
        text: 'Your message has been sent successfully.',
        confirmButtonText: 'OK',
        background: '#fff8f5',
        confirmButtonColor: '#d25614'
      }).then(() => {
        navigate('/');
      });
    }

    // Initialize form label behavior
    const setupInputLabel = (inputId, labelId) => {
      const input = document.getElementById(inputId);
      const label = document.getElementById(labelId);

      if (input && label) {
        input.addEventListener('focus', () => {
          label.classList.add('hidden');
        });

        input.addEventListener('blur', () => {
          if (input.value.trim() === '') {
            label.classList.remove('hidden');
          }
        });
      }
    };

    setupInputLabel('subject', 'subject-label');
    setupInputLabel('message', 'message-label');
    setupInputLabel('email', 'email-label');
    setupInputLabel('name', 'name-label');
  }, [success, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    try {
      await axios.post('https://vasstrinfo.com/api/contact/', formData, {
        headers: {
          Authorization: `Bearer ${authToken?.access}`,
        },
      });
      navigate('/contact?success=true');
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to send your message. Please try again.',
        background: '#fff8f5',
        confirmButtonColor: '#d25614'
      });
    }
  };

  return (
    <Base Content={
      <div className="font-lora bg-transparent min-h-screen ">
        <div className="container mx-auto py-12 px-4 sm:px-6 lg:px-0 max-w-6xl">
          <div className="text-center mx-auto mb-12 max-w-3xl">
            <h1 className="mb-4 text-3xl sm:text-4xl font-bold text-orange-600">Contact Us</h1>
            <div className="w-24 h-1 bg-orange-500 mx-auto rounded-full"></div>
            <p className="mt-4 text-gray-600 text-lg">
              Have questions or feedback? We'd love to hear from you!
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info Cards */}
            <div className="grid grid-cols-2 gap-6 max-md:grid-cols-1 max-md:place-items-center">
              <div className="bg-white rounded-xl p-4 w-full max-w-[225px] h-[165px] shadow-lg hover:shadow-xl transition-all duration-300 border border-orange-100">
                <div className="flex flex-col items-center text-center p-4">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                    <MapPin className="w-6 h-6 text-orange-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Our Location</h3>
                  <p className="text-gray-600">Hyderabad, India</p>
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-4 w-full max-w-[225px] h-[165px] shadow-lg hover:shadow-xl transition-all duration-300 border border-orange-100">
                <div className="flex flex-col items-center text-center p-4">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                    <MailOpen className="w-6 h-6 text-orange-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Email Us</h3>
                  <a 
                    href="mailto:team@vasstr.com" 
                    className="text-orange-600 hover:underline break-all text-sm"
                  >
                    team@vasstr.com
                  </a>
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-4 w-full max-w-[225px] h-[165px] col-span-2 max-md:col-span-1 shadow-lg hover:shadow-xl transition-all duration-300 border border-orange-100">
                <div className="flex flex-col items-center text-center p-4">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                    <Phone className="w-6 h-6 text-orange-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Call Us</h3>
                  <p className="text-gray-600">+91 97043 87878</p>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="w-full h-72 lg:h-[400px] rounded-xl overflow-hidden shadow-lg border border-orange-100 my-auto">
              <iframe 
                className="w-full h-full"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3807.262008528597!2d78.50570408100579!3d17.399209359914927!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb998743c9ee9b%3A0xba484babc9aa812e!2samruta%20ratna%20apartments!5e0!3m2!1sen!2sin!4v1727931040398!5m2!1sen!2sin"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2 bg-white rounded-xl p-6 sm:p-8 shadow-lg border border-orange-100">
              <h2 className="text-2xl font-bold text-orange-600 mb-6">Send us a message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" id="name-label" className="block mb-2 font-medium text-gray-700">Your Name</label>
                    <input 
                      type="text" 
                      id="name" 
                      name="name" 
                      placeholder="Enter your name" 
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition"
                      required 
                    />
                  </div>
                  <div>
                    <label htmlFor="email" id="email-label" className="block mb-2 font-medium text-gray-700">Your Email</label>
                    <input 
                      type="email" 
                      id="email" 
                      name="email" 
                      placeholder="Enter your email" 
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition"
                      required 
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="subject" id="subject-label" className="block mb-2 font-medium text-gray-700">Subject</label>
                  <input 
                    type="text" 
                    id="subject" 
                    name="subject" 
                    placeholder="What's this about?" 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition"
                    required 
                  />
                </div>
                
                <div>
                  <label htmlFor="message" id="message-label" className="block mb-2 font-medium text-gray-700">Your Message</label>
                  <textarea 
                    id="message" 
                    name="message" 
                    placeholder="Write your message here..." 
                    rows="5"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition"
                    required
                  ></textarea>
                </div>
                
                <button 
                  type="submit" 
                  className="w-full py-3 px-6 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-lg shadow-md transition-all duration-300 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
        <div className="h-16 w-full"></div>
      </div>
    } />
  );
};

export default Contact;